import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import * as React from "react";

import { cn, doesDeviceHaveHover } from "../../lib/utils";

interface TooltipTriggerContextType {
	setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const TooltipTriggerContext = React.createContext<TooltipTriggerContextType>({
	setOpen: () => {},
});

// CP - Implemented mobile being able to click tooltips open based on this
// implementation: https://github.com/radix-ui/primitives/issues/1573#issuecomment-2734327586

const TooltipProvider = TooltipPrimitive.Provider;

const Tooltip = (props: TooltipPrimitive.TooltipProps) => {
	const [open, setOpen] = React.useState<boolean>(props.defaultOpen ?? false);
	const hasHover = doesDeviceHaveHover();

	return (
		<TooltipPrimitive.Root
			{...props}
			open={open}
			onOpenChange={setOpen}
			delayDuration={hasHover ? (props.delayDuration ?? 100) : 0}
		>
			<TooltipTriggerContext.Provider value={{ setOpen }}>
				{props.children}
			</TooltipTriggerContext.Provider>
		</TooltipPrimitive.Root>
	);
};

Tooltip.displayName = TooltipPrimitive.Root.displayName;

const TooltipTrigger = React.forwardRef<
	React.ElementRef<typeof TooltipPrimitive.Trigger>,
	React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Trigger>
>(({ children, ...props }, ref) => {
	const hasHover = doesDeviceHaveHover();
	const { setOpen } = React.useContext(TooltipTriggerContext);

	const onClick = React.useCallback(
		(e: React.MouseEvent<HTMLButtonElement>) => {
			setOpen(true);
			if (!hasHover) {
				e.preventDefault();
			} else {
				props.onClick?.(e);
			}
		},
		[setOpen, hasHover, props.onClick],
	);

	return (
		<TooltipPrimitive.Trigger ref={ref} {...props} onClick={onClick}>
			{children}
		</TooltipPrimitive.Trigger>
	);
});

const TooltipContent = React.forwardRef<
	React.ElementRef<typeof TooltipPrimitive.Content>,
	React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content> & { portal?: boolean }
>(({ className, sideOffset = 4, portal = true, ...props }, ref) => {
	const content = (
		<TooltipPrimitive.Content
			ref={ref}
			sideOffset={sideOffset}
			className={cn(
				"z-50 overflow-hidden rounded-md bg-tooltip px-3 py-1.5 text-xs text-white shadow-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 max-w-[300px]",
				className,
			)}
			{...props}
		/>
	);

	if (portal) {
		return <TooltipPrimitive.Portal>{content}</TooltipPrimitive.Portal>;
	}

	return content;
});
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

export { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger };
